import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Card,
  Overline,
  SectionHeader,
  Tag,
  BtnPrimary,
  BtnSecondary,
} from "@/components/cathedral/Primitives";
import PaymentRails from "@/components/cathedral/PaymentRails";
import {
  NEXTXUS_TOKEN_URL, NEXTXUS_TOKEN_ALT,
  RING_OF_6_URL, RING_OF_6_ALT,
} from "@/lib/personaFaces";

const LS_KEY = "nx_license_key";
const BUY_URL_FALLBACK = "https://keywebster.gumroad.com/l/olhsf";

/**
 * Architect directive 2026-02 · Gumroad License Verification flow.
 * $5 = 1 Token. The standard four payment rails (Gumroad → PayPal → JIM →
 * Crypto) render at the top via <PaymentRails/>. Below, the Gumroad license
 * key can be pasted → verified → minted.
 *
 * Backend: POST /api/redeem · GET /api/wallet · GET /api/products
 *          GET /api/wallet/balance/{license_key}
 */
export default function RedeemPage() {
  const [licenseKey, setLicenseKey] = useState(() => {
    if (typeof window === "undefined") return "";
    return window.localStorage?.getItem(LS_KEY) || "";
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  const [wallet, setWallet] = useState(null);
  const [products, setProducts] = useState([]);
  const [balance, setBalance] = useState(null);
  const [member, setMember] = useState(null);
  const [memberBalance, setMemberBalance] = useState(null);

  const refreshMemberBalance = () => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/my-balance`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then(setMemberBalance)
      .catch(() => {});
  };

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/status`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((s) => {
        if (s && s.authenticated) {
          setMember(s.user);
          setMemberBalance({ balance: s.balance, licenses: s.licenses });
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    api.walletAddresses().then(setWallet).catch(() => {});
    api.productsCatalog().then(setProducts).catch(() => setProducts([]));
  }, []);

  useEffect(() => {
    if (!licenseKey || licenseKey.length < 3) return;
    api.walletBalance(licenseKey).then(setBalance).catch(() => setBalance(null));
  }, [licenseKey]);

  const tokenProduct = products.find((p) => p.id === "token");
  const buyUrl = tokenProduct?.buy_url || BUY_URL_FALLBACK;

  const doRedeem = async () => {
    setErr(null);
    setResult(null);
    const key = (licenseKey || "").trim();
    if (!key) {
      setErr("Paste your Gumroad license key.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.redeemLicense(key);
      if (res.success) {
        setResult(res);
        window.localStorage?.setItem(LS_KEY, key);
        // Member perks: link this license to the signed-in account
        if (member) {
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/link-license`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ license_key: key }),
          }).then(() => refreshMemberBalance()).catch(() => {});
        }
        // refresh balance
        const b = await api.walletBalance(key).catch(() => null);
        if (b) setBalance(b);
      } else {
        setErr(res.error || "Invalid or already redeemed");
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Network error");
    } finally {
      setSubmitting(false);
    }
  };

  const copy = (text) => navigator.clipboard?.writeText(text);

  return (
    <div className="px-6 lg:px-12 py-10 max-w-5xl mx-auto" data-testid="redeem-page">
      {/* ── Hero ───────────────────────────────────────────────────────── */}
      <div className="mb-10 flex flex-col-reverse md:flex-row md:items-center gap-8 md:gap-10">
        <div className="flex-1 min-w-0">
          <Overline>Storefront · Tokens & Services</Overline>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-[0.02em] text-glow-cyan leading-none mt-3">
            One Token. Five Dollars.
          </h1>
          <p className="mt-6 max-w-2xl text-[var(--text-2)] leading-relaxed">
            Buy a NextXus Access Token for <span className="text-glow-gold">$5</span> on Gumroad,
            or pay with crypto. Each token unlocks one AI consultation, Truth Gate verification, or
            Library download. No subscriptions. No middlemen.
          </p>
        </div>
        <div className="shrink-0">
          <img
            src={NEXTXUS_TOKEN_URL}
            alt={NEXTXUS_TOKEN_ALT}
            data-testid="redeem-hero-token-image"
            className="w-44 h-44 sm:w-52 sm:h-52 object-cover rounded-full"
            style={{
              border:    "2px solid #ffb200",
              boxShadow: "0 0 28px rgba(255,189,0,0.55), 0 0 64px rgba(255,189,0,0.22)",
            }}
          />
        </div>
      </div>

      {/* ── Standard 4-Rail Payment Block ────────────────────────────── */}
      <PaymentRails
        className="mb-10"
        heading="Four ways to pay"
        subtitle="Gumroad · PayPal · JIM · Crypto — $5 = 1 Access Token"
      />

      {/* ── Rail 1 · Gumroad ───────────────────────────────────────────── */}
      <Card glow="violet" className="p-8 mb-10" data-testid="gumroad-rail">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <SectionHeader
            title="Pay with Gumroad"
            subtitle="Buy on Gumroad → paste the license key here → mint your tokens"
          />
          <Tag tone="cite">RAIL 1</Tag>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <a href={buyUrl} target="_blank" rel="noopener noreferrer" data-testid="gumroad-buy-link">
            <BtnPrimary data-testid="gumroad-buy-btn">▼ Purchase Token · $5</BtnPrimary>
          </a>
          <a
            href="https://keywebster.gumroad.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="gumroad-fallback-link"
            className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--text-3)] hover:text-glow-violet transition-colors"
          >
            Or buy on Gumroad →
          </a>
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--text-3)]">
            1 purchase = 1 token = 1 use
          </span>
        </div>

        <div className="mt-8">
          <label className="block">
            <span className="font-mono text-[9px] uppercase tracking-[0.32em] text-[var(--text-3)]">
              Paste your Gumroad license key
            </span>
            <input
              type="text"
              value={licenseKey}
              onChange={(e) => setLicenseKey(e.target.value)}
              placeholder="XXXX-XXXX-XXXX-XXXX"
              data-testid="license-key-input"
              autoComplete="off"
              spellCheck={false}
              className="mt-2 w-full bg-[var(--void-2)] border border-[var(--chrome)] focus:border-[var(--violet)] outline-none px-4 py-3 font-mono text-sm tracking-[0.18em] text-[var(--text)] transition-colors"
            />
          </label>
          <div className="mt-5 flex items-center gap-3 flex-wrap">
            <BtnPrimary
              onClick={doRedeem}
              disabled={submitting}
              data-testid="redeem-submit-btn"
            >
              {submitting ? "VERIFYING…" : "✓ Verify & Mint Token"}
            </BtnPrimary>
            <Link to="/library">
              <BtnSecondary data-testid="redeem-browse-library">
                Browse Library →
              </BtnSecondary>
            </Link>
          </div>
        </div>

        {err && (
          <div
            className="mt-6 p-4 border border-[var(--red)] bg-[rgba(255,46,99,0.08)]"
            data-testid="redeem-error"
          >
            <Tag tone="conflict">REJECTED</Tag>
            <p className="font-mono text-sm text-glow-red mt-2 break-words">{err}</p>
          </div>
        )}

        {result && result.success && (
          <div
            className="mt-6 p-5 border border-[var(--gold)] bg-[rgba(255,189,0,0.06)]"
            data-testid="redeem-success"
          >
            <Tag tone="pass">VERIFIED · SEALED</Tag>
            <h3 className="font-display text-2xl text-glow-gold mt-3 leading-tight">
              {result.tokens_granted > 0
                ? `+${result.tokens_granted} token${result.tokens_granted === 1 ? "" : "s"} minted`
                : "Already redeemed"}
            </h3>
            <div className="mt-3 grid sm:grid-cols-2 gap-4 font-mono text-[10px] uppercase tracking-[0.22em]">
              <div>
                <div className="text-[var(--text-3)]">Wallet balance</div>
                <div className="text-glow-cyan text-2xl mt-1" data-testid="redeem-balance">
                  {result.balance} token{result.balance === 1 ? "" : "s"}
                </div>
              </div>
              <div>
                <div className="text-[var(--text-3)]">License key</div>
                <div className="text-glow-violet text-sm mt-1 break-all">{result.license_key}</div>
              </div>
            </div>
          </div>
        )}

        {member && memberBalance && (
          <div className="mt-6 p-4 border border-[var(--gold,#FFD700)]" style={{ background: "rgba(255,215,0,0.05)" }} data-testid="member-perks-strip">
            <div className="font-mono text-[11px] uppercase tracking-[0.2em]" style={{ color: "#FFD700" }}>
              ◈ {member.name || member.email} · account balance
            </div>
            <div className="text-2xl mt-1" style={{ color: "#00FFCC" }} data-testid="member-perks-balance">
              {memberBalance.balance} token{memberBalance.balance === 1 ? "" : "s"} · {memberBalance.licenses} linked license{memberBalance.licenses === 1 ? "" : "s"}
            </div>
            <div className="font-mono text-[11px] mt-1" style={{ color: "rgba(224,224,224,0.6)" }}>
              Every key you redeem while signed in follows your account automatically.
            </div>
          </div>
        )}
        {balance && balance.found && !result && (
          <div className="mt-6 p-4 border border-[var(--cyan)] bg-[rgba(0,229,255,0.05)]" data-testid="redeem-existing-balance">
            <Tag tone="fact">EXISTING WALLET</Tag>
            <p className="font-mono text-sm text-[var(--text-2)] mt-2">
              This key has{" "}
              <span className="text-glow-cyan text-base">{balance.balance}</span>{" "}
              token{balance.balance === 1 ? "" : "s"} remaining.
            </p>
          </div>
        )}
      </Card>

      {/* ── Rail 2 · Crypto ────────────────────────────────────────────── */}
      <Card glow="gold" className="p-8 mb-10" data-testid="crypto-rail">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <SectionHeader
            title="Pay with Crypto"
            subtitle="Send $5 USD equivalent · 1 token per $5 · DM tx hash to mint"
          />
          <Tag tone="assumption">RAIL 2</Tag>
        </div>

        {wallet && (
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            <div
              className="p-5 border border-[var(--violet)] bg-[var(--void-2)]"
              data-testid="wallet-eth-card"
            >
              <div className="font-mono text-[9px] uppercase tracking-[0.32em] text-[var(--text-3)] mb-2">
                USDC / ETH · Ethereum
              </div>
              <div className="font-mono text-xs text-glow-cyan break-all select-all" data-testid="wallet-eth">
                {wallet.usdc_eth}
              </div>
              <button
                onClick={() => copy(wallet.usdc_eth)}
                className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--text-3)] hover:text-glow-cyan transition-colors"
                data-testid="copy-eth-btn"
              >
                ⎘ COPY
              </button>
            </div>
            <div
              className="p-5 border border-[var(--gold)] bg-[var(--void-2)]"
              data-testid="wallet-btc-card"
            >
              <div className="font-mono text-[9px] uppercase tracking-[0.32em] text-[var(--text-3)] mb-2">
                BTC · Bitcoin (segwit)
              </div>
              <div className="font-mono text-xs text-glow-gold break-all select-all" data-testid="wallet-btc">
                {wallet.btc}
              </div>
              <button
                onClick={() => copy(wallet.btc)}
                className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--text-3)] hover:text-glow-gold transition-colors"
                data-testid="copy-btc-btn"
              >
                ⎘ COPY
              </button>
            </div>
          </div>
        )}
        <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--text-3)]">
          Peg · {wallet?.peg || "1 token = $5 USD"} · After sending, post the tx hash to{" "}
          <Link to="/board" className="text-glow-violet hover:text-glow-cyan">/board</Link>{" "}
          and the Architect mints manually.
        </p>
      </Card>

      {/* ── Catalog ────────────────────────────────────────────────────── */}
      <Card className="p-8 mb-10" data-testid="catalog-card">
        <SectionHeader title="What tokens buy" subtitle="Six services priced in tokens" />
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((p) => (
            <div
              key={p.id}
              className="p-5 border border-[var(--chrome)] bg-[var(--void-2)] hover:border-[var(--cyan)] transition-colors"
              data-testid={`product-${p.id}`}
            >
              <div className="font-display text-lg text-glow-cyan leading-tight">{p.name}</div>
              <p className="mt-2 font-mono text-[11px] text-[var(--text-2)] leading-relaxed">
                {p.description}
              </p>
              <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-glow-gold">
                {p.price_usd != null
                  ? `$${p.price_usd}`
                  : `${p.price_tokens} token${p.price_tokens === 1 ? "" : "s"}`}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* ── Ring of 6 · Starter Ring Questions tier ──────────────────── */}
      <Card glow="cyan" className="p-0 mb-10 overflow-hidden" data-testid="ring-of-6-tier-card">
        <div className="relative">
          <img
            src={RING_OF_6_URL}
            alt={RING_OF_6_ALT}
            data-testid="ring-of-6-hero-image"
            className="w-full h-56 sm:h-64 object-cover"
          />
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(2,3,12,0.10) 0%, rgba(2,3,12,0.55) 55%, rgba(2,3,12,0.92) 100%)",
            }}
          />
          <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8">
            <Overline tone="muted">Ring of 6 · Verification Layer</Overline>
            <h3 className="font-display text-2xl sm:text-3xl text-glow-cyan mt-2 leading-tight">
              Starter Ring Questions
            </h3>
            <p className="mt-2 font-mono text-[12px] sm:text-[13px] text-[var(--text-2)] max-w-2xl leading-relaxed">
              Six perspectives. One verified answer. The starter tier ships your question through
              the six-lens deliberation engine and returns a Truth-Gate-sealed verdict in plain
              English.
            </p>
          </div>
        </div>
        <div className="p-6 sm:p-8 flex flex-wrap items-center justify-between gap-4">
          <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[var(--text-3)]">
            5 questions · 5 tokens · $25 starter pack
          </div>
          <div className="flex flex-wrap gap-3">
            <a href={buyUrl} target="_blank" rel="noopener noreferrer" data-testid="ring-of-6-buy-link">
              <BtnPrimary data-testid="ring-of-6-buy-btn">▼ Get Starter Pack · $25</BtnPrimary>
            </a>
            <Link to="/execute">
              <BtnSecondary data-testid="ring-of-6-try-btn">Try the Ring →</BtnSecondary>
            </Link>
          </div>
        </div>
      </Card>

      <Card className="p-8" glow="cyan" data-testid="how-it-works-card">
        <SectionHeader title="How it works" />
        <ol className="mt-5 space-y-3 font-mono text-[12px] leading-relaxed text-[var(--text-2)] list-decimal list-inside">
          <li>Click <span className="text-glow-violet">Buy Token · $5 on Gumroad</span> above (or send crypto).</li>
          <li>Gumroad emails you a license key like <span className="font-display text-glow-gold">XXXX-XXXX-XXXX-XXXX</span>.</li>
          <li>Paste the key in the form. We verify it via Gumroad&apos;s License API.</li>
          <li>One token is minted to your wallet. Spend it via <code className="text-glow-cyan">POST /api/execute</code> or any persona chat.</li>
          <li>Buy again to top up. Codes are tracked by the Gumroad `uses` counter, no double-mints.</li>
        </ol>
      </Card>
    </div>
  );
}
